"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const channel_1 = require("./channel");
const cancellable_1 = require("./utils/cancellable");
class GizmoRuntime {
    receiveChannel = new channel_1.ReceiveChannel();
    seq = 0;
    /** Called by native, to send a message from native to web */
    receiveMessage(message) {
        this.receiveChannel.emit(message);
        return Promise.resolve();
    }
    /** Calls out to native to perform haptic feedback on the device */
    performHaptic(style) {
        window.webkit?.messageHandlers?.hapticHandler?.postMessage(this.makeMessage({ kind: 'impact', style }));
    }
    /** Calls out to native to perform text generation */
    generateText(input) {
        const handler = window.webkit?.messageHandlers?.textGenerationHandler;
        if (!handler) {
            return Promise.reject(new Error('Not available'));
        }
        return this.performHandlerRequest(handler, { prompt: input.prompt }, 
        // Backstop 30s timeout, the actual rpc may fail sooner
        30000);
    }
    /**
     * Adds a listener for shake events
     * The listener will be called on the "end" of a shake event
     * @returns A function that can be called to remove the listener
     */
    addShakeListener(listener) {
        const handler = window.webkit?.messageHandlers?.deviceMotionHandler;
        if (!handler) {
            console.warn('Shake handler not available');
            return () => { }; // No-op cancellation if handler isn't available
        }
        return this.createSubscription(handler, { type: 'shake' }, () => {
            listener();
        });
    }
    /**
     * Adds a listener for motion events
     * The listener will be called on each motion event, with the latest measurements.
     * See MotionEvent interface for more details.
     * @returns A function that can be called to remove the listener
     */
    addMotionListener(listener) {
        const handler = window.webkit?.messageHandlers?.deviceMotionHandler;
        if (!handler) {
            console.warn('Motion handler not available');
            return () => { }; // No-op cancellation if handler isn't available
        }
        return this.createSubscription(handler, { type: 'motion' }, response => {
            if (response.motion) {
                listener(response.motion);
            }
        });
    }
    /**
     * Creates a subscription to an event stream from a native handler
     * @param handler The native handler to subscribe to
     * @param request The request to send to the handler
     * @param callback The callback to call when an event is received
     * @returns A function that can be called to cancel the subscription
     */
    createSubscription(handler, request, callback) {
        const message = this.makeSubscriptionMessage(request);
        const subscription = this.receiveChannel.subscribe(message.endpoint, response => {
            if (response.error !== undefined) {
                console.error(`Subscription error: ${response.error}`);
                return;
            }
            callback(response);
        });
        // Post the initial message to start the subscription
        handler.postMessage(message);
        // Return a cancellation function
        return () => {
            // First cancel the subscription to prevent any more callbacks
            subscription.cancel();
            // Then tell the native side to stop sending events
            handler.postMessage({
                endpoint: message.endpoint,
                payload: 'unsubscribe',
            });
        };
    }
    /**
     * Performs a standard request/response pattern where a native handler is invoked and a
     * response message is expected to be sent back
     */
    performHandlerRequest(handler, request, timeoutMs) {
        return new Promise((resolve, reject) => {
            const message = this.makeMessage(request);
            const subscription = this.receiveChannel.once(message.endpoint, response => {
                timeoutCancellable.cancel();
                if (response.error !== undefined) {
                    reject(new Error(`Error: ${response.error}`));
                }
                else {
                    resolve(response);
                }
            });
            // It's important to have a maximum timeout here in case there was a fatal error in the
            // native side that prevents a response for any reason
            const timeoutCancellable = (0, cancellable_1.setTimeoutCancellable)(() => {
                subscription.cancel();
                reject(new Error('Request timed out'));
            }, timeoutMs);
            handler.postMessage(message);
        });
    }
    makeMessage(payload) {
        return {
            endpoint: (this.seq++).toString(),
            payload,
        };
    }
    makeSubscriptionMessage(payload) {
        return {
            endpoint: (this.seq++).toString(),
            payload: { subscribe: payload },
        };
    }
}
exports.default = GizmoRuntime;
//# sourceMappingURL=data:application/json;base64,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